import { CalendarOutlined, ExportOutlined, UserOutlined } from '@ant-design/icons';
import { DrawerHeaderAction, SharedEmpty, SharedSpinner } from '@components';
import { useRelatedOrders, useRoleAccess, useTenantVat, useView } from '@hooks';
import { L } from '@lib';
import { OrderStatus, ServiceOrderStatusTag, ServiceOrderStatusTagObjectKey } from '@models';
import { Order, Vehicle } from '@services';
import { ServiceBasket, useStore } from '@stores';
import { Button, Checkbox, Col, Row, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { Suspense, lazy, useMemo, useState } from 'react';
// Code-split like every other host of the booking drawer.
const BookingDrawer = lazy(() => import('../Drawer/BookingDrawer/BookingDrawer.container'));

interface IProps {
  order: Order | undefined;
}

export const RelatedOrders: React.FC<IProps> = observer(
  ({ order }) => {
    const { isMobile } = useView();
    const { orderStore, serviceStore } = useStore();
    const [openModal, setOpenModal] = useState(false);
    // The booking only needs what the order already says about its vehicle, so it is built from
    // that rather than fetched. It has no id; the details drawer loads the full record on open.
    const vehicle = useMemo<Vehicle | undefined>(
      () =>
        order?.registrationNumber
          ? ({
              id: '',
              registrationNumber: order.registrationNumber,
              combinedVin: order.vin ?? '',
              combinedModel: order.vehicleModel,
              mileage: order.mileage,
            } as Vehicle)
          : undefined,
      [order?.registrationNumber, order?.vin, order?.vehicleModel, order?.mileage]
    );
    const vat = useTenantVat();

    // The booking drawer gets a basket of its own: serviceStore's holds the open order's unsaved
    // services, and the drawer clears whatever basket it books from.
    const basket = useMemo(
      () => new ServiceBasket(serviceStore.selectServices, vat),
      [serviceStore, vat]
    );

    const { order: access } = useRoleAccess();


    const { relatedOrders: orders, relatedLoading: loading } = useRelatedOrders(order?.id);

    const isDisabled =
      order?.status === OrderStatus.Ready ||
      order?.status === OrderStatus.Paid ||
      order?.status === OrderStatus.Approval;

    // The shared status tags carry a 200px min-width for tables; on a phone that is the whole row.
    const statusTag = (status: string) => {
      const tag = ServiceOrderStatusTag[status as ServiceOrderStatusTagObjectKey];
      return isMobile && React.isValidElement<{ style?: React.CSSProperties }>(tag)
        ? React.cloneElement(tag, { style: { ...tag.props.style, minWidth: 0 } })
        : tag;
    };

    // Another job opens beside this one, never in its place: the drawer holds unsaved work.
    const openOrder = (id: string) => window.open(`/orders/${id}`, '_blank', 'noopener,noreferrer');

    const canCreate = !isDisabled && access.createRelated;
    const createButton = (
      <Tooltip title={vehicle ? undefined : 'Add a registration number to this job first.'}>
        <Button type="primary" disabled={!vehicle} onClick={() => setOpenModal(true)}>
          Create separate work
        </Button>
      </Tooltip>
    );

    return (
      <SharedSpinner loading={loading}>
        {/* In a drawer the action sits in its header; elsewhere it stays above the list. */}
        {canCreate && orders.length > 0 && (
          <DrawerHeaderAction
            inline={
              <Row justify="end" style={{ marginBottom: 10 }}>
                <Col>{createButton}</Col>
              </Row>
            }
          >
            {createButton}
          </DrawerHeaderAction>
        )}
        {!loading && orders.length === 0 && (
          <SharedEmpty
            description="No related work for this vehicle and customer yet"
            action={canCreate && createButton}
          />
        )}
        {orders.map((x) => {
          return (
            <div
              key={x.id}
              style={{
                background: '#fafafa',
                border: '1px solid #eee',
                borderRadius: 8,
                padding: '12px 16px',
                marginBottom: 10,
              }}
            >
              {/* Checkbox · details (takes the slack, truncates) · status (never squeezed). The old
                  24-column grid gave the status less room than the checkbox took, so it fell out. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                <Checkbox
                  style={{ margin: 0 }}
                  checked={orderStore.relatedOrderChecked(x.id)}
                  disabled={x.status !== OrderStatus.Completed}
                  onChange={(e) => orderStore.toggleRelatedCheck(x.id, e.target.checked)}
                />

                <div
                  role="link"
                  tabIndex={0}
                  title="Open this job in a new tab"
                  style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                  onClick={() => openOrder(x.id)}
                  onKeyDown={(e) => e.key === 'Enter' && openOrder(x.id)}
                >
                  <div
                    style={{
                      fontWeight: 'bold',
                      fontSize: 14,
                      color: 'rgba(0, 0, 0, 0.85)',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {x.title}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'rgba(0, 0, 0, 0.45)',
                      marginTop: 4,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '2px 12px',
                    }}
                  >
                    <span>
                      <UserOutlined style={{ marginRight: 4, fontSize: 13 }} />
                      {(x.user && x.user.fullname) || L('No technician')}
                    </span>
                    {x.dropOffDate && (
                      <span>
                        <CalendarOutlined style={{ marginRight: 4, fontSize: 13 }} />
                        {dayjs(x.dropOffDate).format('DD/MM/YYYY HH:mm')}
                      </span>
                    )}
                  </div>
                </div>

                {/* On a phone the status and View drop to their own line under the details; the
                    status tag also gives up its desktop min-width there. */}
                <div
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isMobile ? 'space-between' : undefined,
                    flexBasis: isMobile ? '100%' : undefined,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    paddingLeft: isMobile ? 28 : undefined,
                    gap: 8,
                    fontSize: 12,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {statusTag(x.status)}
                  <Button size="small" icon={<ExportOutlined />} onClick={() => openOrder(x.id)}>
                    View
                  </Button>
                </div>
              </div>
            </div>
          );
        })}

        {/* The same drawer as a fresh booking - same form, same footer - linked to this job's series. */}
        {openModal && vehicle && order && (
          <Suspense fallback={null}>
            <BookingDrawer
              mode="booking"
              hideButton
              showDrawer={openModal}
              setShowDrawer={setOpenModal}
              initialCustomer={order.customer}
              initialVehicle={vehicle}
              initialVehicleOwner={order.vehicleOwner}
              relationId={order.relationId}
              basket={basket}
            />
          </Suspense>
        )}
      </SharedSpinner>
    );
  }
);
